import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Activity, Check, CheckCircle2, ChevronLeft, Eye, Loader2, Phone, Truck, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const CourierServicesPage = () => {
  const [form, setForm] = useState({
    pickup_address: "",
    dropoff_address: "",
    document_type: "legal_documents",
    requires_signature: true,
    urgency: "standard"
  });
  const [quote, setQuote] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [tracking, setTracking] = useState(null);
  
  useEffect(() => {
    // Check service status on load
    axios.get(`${API}/integrations/courier/status`).then(res => setServiceStatus(res.data)).catch(() => {});
  }, []);
  
  const getQuote = async () => {
    setLoading(true);
    setQuote(null);
    try {
      const res = await axios.post(`${API}/integrations/courier/quote`, {
        pickup_address: { formatted_address: form.pickup_address },
        dropoff_address: { formatted_address: form.dropoff_address },
        document_type: form.document_type,
        requires_signature: form.requires_signature,
        urgency: form.urgency
      });
      setQuote(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const createDelivery = async () => {
    if (!quote) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/integrations/courier/create`, {
        quote_id: quote.quote_id,
        sender: { first_name: "Law", last_name: "Firm", phone: "+15551234567", email: "firm@example.com" },
        recipient: { first_name: "Court", last_name: "Clerk", phone: "+15559876543", email: "clerk@court.gov" },
        pickup_address: { formatted_address: form.pickup_address },
        dropoff_address: { formatted_address: form.dropoff_address },
        document_description: "Legal Documents",
        requires_signature: form.requires_signature
      });
      setDelivery(res.data);
      setQuote(null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const trackDelivery = async () => {
    if (!delivery) return;
    try {
      const res = await axios.get(`${API}/integrations/courier/track/${delivery.delivery_id}`);
      setTracking(res.data);
    } catch (e) {
      console.error(e);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="courier-services-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools/services-hub" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Services Hub
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Legal Courier Services</h1>
            {serviceStatus && (
              <Badge variant={serviceStatus.mode === 'live' ? 'default' : 'secondary'}>
                {serviceStatus.mode === 'live' ? 'Live' : 'Demo Mode'}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">Same-day legal document delivery powered by Uber Direct</p>
        </div>
        
        {/* Features Grid */}
        {serviceStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {Object.entries(serviceStatus.features || {}).map(([key, enabled]) => (
              <div key={key} className={`p-3 rounded-lg border ${enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  {enabled ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-slate-400" />}
                  <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Quote Form */}
        {!delivery && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Get Instant Quote</CardTitle>
              <CardDescription>Enter pickup and delivery details for an instant price estimate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Pickup Address</Label>
                <Input value={form.pickup_address} onChange={e => setForm({...form, pickup_address: e.target.value})} placeholder="123 Main St, Los Angeles, CA 90001" />
              </div>
              <div>
                <Label>Dropoff Address</Label>
                <Input value={form.dropoff_address} onChange={e => setForm({...form, dropoff_address: e.target.value})} placeholder="456 Court St, Los Angeles, CA 90012" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Document Type</Label>
                  <Select value={form.document_type} onValueChange={v => setForm({...form, document_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="legal_documents">Legal Documents</SelectItem>
                      <SelectItem value="court_filing">Court Filing</SelectItem>
                      <SelectItem value="subpoena">Subpoena/Summons</SelectItem>
                      <SelectItem value="contracts">Contracts</SelectItem>
                      <SelectItem value="evidence">Evidence Package</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Urgency</Label>
                  <Select value={form.urgency} onValueChange={v => setForm({...form, urgency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (2-4 hrs)</SelectItem>
                      <SelectItem value="same_day">Same Day</SelectItem>
                      <SelectItem value="urgent">Urgent (&lt;1 hr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="signature" 
                  checked={form.requires_signature} 
                  onChange={e => setForm({...form, requires_signature: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="signature" className="cursor-pointer">Require signature on delivery (+$2.99)</Label>
              </div>
              <Button onClick={getQuote} disabled={loading || !form.pickup_address || !form.dropoff_address} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />}
                Get Quote
              </Button>
              
              {quote && (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Estimated Price:</span>
                    <span className="text-2xl font-bold text-emerald-700">${quote.estimated_price}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Distance:</span> {quote.distance_miles} miles</div>
                    <div><span className="text-muted-foreground">Service:</span> {quote.service_level}</div>
                    <div><span className="text-muted-foreground">Pickup by:</span> {new Date(quote.estimated_pickup).toLocaleTimeString()}</div>
                    <div><span className="text-muted-foreground">Delivered by:</span> {new Date(quote.estimated_delivery).toLocaleTimeString()}</div>
                  </div>
                  <p className="text-xs text-muted-foreground">Quote valid until: {new Date(quote.valid_until).toLocaleTimeString()}</p>
                  <Button onClick={createDelivery} className="w-full" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Confirm & Schedule Pickup
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Active Delivery Tracking */}
        {delivery && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                Delivery Scheduled
              </CardTitle>
              <CardDescription>Reference: {delivery.external_id}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">Status:</span>
                  <Badge>{tracking?.status || delivery.status}</Badge>
                </div>
                {tracking?.courier_name && (
                  <div className="text-sm">
                    <p><span className="text-muted-foreground">Courier:</span> {tracking.courier_name}</p>
                    <p><span className="text-muted-foreground">Phone:</span> {tracking.courier_phone}</p>
                  </div>
                )}
                {tracking?.estimated_arrival && (
                  <p className="text-sm"><span className="text-muted-foreground">ETA:</span> {new Date(tracking.estimated_arrival).toLocaleTimeString()}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={trackDelivery} variant="outline" className="flex-1">
                  <Activity className="h-4 w-4 mr-2" /> Refresh Status
                </Button>
                <Button onClick={() => window.open(delivery.tracking_url, '_blank')} variant="outline" className="flex-1">
                  <Eye className="h-4 w-4 mr-2" /> Live Tracking
                </Button>
              </div>
              <Button onClick={() => { setDelivery(null); setTracking(null); }} variant="ghost" className="w-full">
                Schedule Another Delivery
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default CourierServicesPage;
